import React, { useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { Box } from "@mui/material";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"]
  ],
  imageResize: {
    modules: ["Resize", "DisplaySize"]
  }
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
  "image",
  "width"
];

// Shared react-quill wrapper (toolbar config + theme-aware styling) so ticket/task
// description fields don't each duplicate it. Pass `enableImagePaste` to let users
// paste an image straight from the clipboard into the editor's "image" embed.
function RichTextEditor({
  value,
  onChange,
  placeholder = "Enter description",
  className,
  enableImagePaste = false,
  minHeight
}) {
  const quillRef = useRef(null);

  useEffect(() => {
    if (!enableImagePaste) {
      return undefined;
    }

    const editor = quillRef.current?.getEditor();

    if (!editor) {
      return undefined;
    }

    const handlePaste = (event) => {
      const items = event.clipboardData?.items;

      if (!items) {
        return;
      }

      const imageItem = Array.from(items).find((item) => item.type.startsWith("image/"));

      if (!imageItem) {
        return;
      }

      event.preventDefault();

      const file = imageItem.getAsFile();

      if (!file) {
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const range = editor.getSelection(true);
        editor.insertEmbed(range ? range.index : editor.getLength(), "image", reader.result, "user");
      };

      reader.readAsDataURL(file);
    };

    const editorRoot = editor.root;
    editorRoot.addEventListener("paste", handlePaste);

    return () => {
      editorRoot.removeEventListener("paste", handlePaste);
    };
  }, [enableImagePaste]);

  return (
    <Box
      sx={(theme) => ({
        flex: 1,
        display: "flex",
        flexDirection: "column",
        "& .quill": { display: "flex", flexDirection: "column", flex: 1 },
        "& .ql-toolbar": {
          borderColor: theme.palette.divider,
          bgcolor: "background.paper",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        },
        "& .ql-container": {
          flex: 1,
          overflowY: "auto",
          borderColor: theme.palette.divider,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        },
        "& .ql-editor": {
          bgcolor: "background.paper",
          color: "text.primary",
          minHeight,
        },
        "& .ql-editor.ql-blank::before": {
          color: theme.palette.text.secondary,
          fontStyle: "normal",
        },
        "& .ql-picker-label": {
          color: theme.palette.text.primary,
        },
        "& .ql-picker-options": {
          bgcolor: "background.paper",
          color: "text.primary",
        },
        "& .ql-stroke": {
          stroke: theme.palette.text.secondary,
        },
        "& .ql-fill": {
          fill: theme.palette.text.secondary,
        },
      })}
    >
      <ReactQuill
        ref={quillRef}
        className={className}
        value={value}
        onChange={onChange}
        modules={quillModules}
        formats={quillFormats}
        placeholder={placeholder}
        style={{ fontSize: 16 }}
      />
    </Box>
  );
}

export default RichTextEditor;
